import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function FindAndReview({ user }) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'manager';
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [newProfile, setNewProfile] = useState({ name: '', address: '', phone: '', neighborhood: '', about: '' });
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const navigate = useNavigate();

  // Handle return from signup with pending review
  useEffect(() => {
    const pending = searchParams.get('pending');
    if (pending && user) {
      const pendingName = sessionStorage.getItem('pendingReviewName');
      const pendingAddress = sessionStorage.getItem('pendingReviewAddress');
      const pendingPhone = sessionStorage.getItem('pendingReviewPhone');
      if (pendingName) {
        setNewProfile({ name: pendingName, address: pendingAddress || '', phone: pendingPhone || '', neighborhood: '', about: '' });
        setShowAddForm(true);
        sessionStorage.removeItem('pendingReviewName');
        sessionStorage.removeItem('pendingReviewAddress');
        sessionStorage.removeItem('pendingReviewPhone');
      }
    }
  }, [user, searchParams]);
  const isManager = type === 'manager';

  useEffect(() => {
    const checkGoogle = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setGoogleReady(true);
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      } else {
        setTimeout(checkGoogle, 100);
      }
    };
    checkGoogle();
  }, []);

  const searchPlaces = useCallback(async (input, cityFilter) => {
    if (!input.trim() || input.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Tenant search — RenterRep database only, no Google Places
    if (!isManager) {
      const { data } = await supabase.from('tenants').select('*').ilike('name', `%${input}%`).limit(6);
      if (data && data.length > 0) {
        setSuggestions(data.map(d => ({
          place_id: 'db_' + d.id,
          description: d.name,
          structured_formatting: { main_text: d.name, secondary_text: 'RenterRep verified tenant' },
          isDb: true, dbData: d,
        })));
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
      return;
    }

    if (!googleReady) return;

    const query = `${input} property management ${cityFilter ? cityFilter : ''}`.trim();

    try {
      // Try new API first
      if (window.google.maps.places.AutocompleteSuggestion) {
        const { suggestions: results } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: sessionTokenRef.current,
          includedRegionCodes: ['us'],
        });
        if (results && results.length > 0) {
          const formatted = results.slice(0, 6).map(s => ({
            place_id: s.placePrediction?.placeId,
            description: s.placePrediction?.text?.text || '',
            structured_formatting: {
              main_text: s.placePrediction?.mainText?.text || '',
              secondary_text: s.placePrediction?.secondaryText?.text || '',
            },
            placePrediction: s.placePrediction,
          }));
          setSuggestions(formatted);
          setShowDropdown(true);
          return;
        }
      }
    } catch(e) {}

    // Fallback to old API
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions({
        input: query,
        componentRestrictions: { country: 'us' },
        types: ['establishment'],
        sessionToken: sessionTokenRef.current,
      }, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.slice(0, 6));
          setShowDropdown(true);
        } else {
          setSuggestions([]);
          setShowDropdown(false);
        }
      });
    } catch(e) {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [googleReady, isManager]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!name.trim() || name.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(() => searchPlaces(name, city), 400);
  }, [name, city, searchPlaces]);

  const handleSelectSuggestion = async (suggestion) => {
    setShowDropdown(false);
    if (suggestion.isDb) {
      setSelected(suggestion.dbData);
      setName(suggestion.dbData.name);
      return;
    }

    const mainText = suggestion.structured_formatting?.main_text || suggestion.description?.split(',')[0];
    setName(mainText);

    try {
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv);
      const service = new window.google.maps.places.PlacesService(map);
      service.getDetails({
        placeId: suggestion.place_id,
        fields: ['name', 'formatted_address', 'formatted_phone_number'],
        sessionToken: sessionTokenRef.current,
      }, async (place, status) => {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const { data: existing } = await supabase.from('managers').select('*').ilike('name', `%${place.name}%`).limit(1);
          if (existing && existing.length > 0) {
            setSelected(existing[0]);
          } else {
            setNewProfile({
              name: place.name || '',
              address: place.formatted_address || '',
              phone: place.formatted_phone_number || '',
              neighborhood: city || '',
              about: '',
            });
            setSelected({ google: true, name: place.name, address: place.formatted_address, phone: place.formatted_phone_number });
          }
        }
      });
    } catch(e) {}
  };

  const handleGetLocation = () => {
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === 'OK') {
            const comps = results[0]?.address_components || [];
            const cityComp  = comps.find(c => c.types.includes('locality'));
            const stateComp = comps.find(c => c.types.includes('administrative_area_level_1'));
            const parts = [cityComp?.long_name, stateComp?.short_name].filter(Boolean);
            if (parts.length) setCity(parts.join(', '));
          }
          setLocationLoading(false);
        });
      },
      () => { setLocationLoading(false); alert('Could not get your location.'); }
    );
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newProfile.name.trim()) { alert('Please enter a name.'); return; }
    // Adding any profile requires auth — RLS on managers/tenants restricts INSERT
    // to authenticated role. Stash the form, bounce through signup, restore on return.
    if (!user) {
      sessionStorage.setItem('pendingReviewName', newProfile.name);
      sessionStorage.setItem('pendingReviewAddress', newProfile.address || '');
      sessionStorage.setItem('pendingReviewPhone', newProfile.phone || '');
      navigate('/signup?redirect=' + encodeURIComponent('/find?type=' + type + '&pending=1'));
      return;
    }
    if (!isManager && user?.role !== 'manager') {
      alert('Only property management/managers can add new tenants.');
      return;
    }
    setAdding(true);
    const initials = newProfile.name.split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase();

    let result;
    if (isManager) {
      result = await supabase.from('managers').insert({
        name: newProfile.name, address: newProfile.address,
        phone: newProfile.phone, neighborhood: newProfile.neighborhood,
        about: newProfile.about, initials, rating: 0, review_count: 0,
      }).select().single();
    } else {
      result = await supabase.from('tenants').insert({
        name: newProfile.name, initials,
      }).select().single();
    }

    const { data, error } = result;
    if (!error && data) { setAdded(data); }
    else {
      console.error('Insert error:', error);
      alert(error?.message || 'Something went wrong.');
    }
    setAdding(false);
  };

  if (added) {
    return (
      <div className="page-wrap">
        <div className="container" style={{ paddingTop: 64, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0F2C4E', marginBottom: 8 }}>{added.name} has been added!</h1>
          <p style={{ fontSize: 14, color: '#6C757D', marginBottom: 32 }}>Their profile is now live. You can now write a review.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={() => navigate(user ? '/review/' + added.id : '/signup?redirect=' + encodeURIComponent('/review/' + added.id))} style={{ background: '#C9972A', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Write a review →</button>
            <button onClick={() => navigate('/profile/' + added.id)} style={{ background: 'transparent', color: '#0F2C4E', border: '1.5px solid #0F2C4E', padding: '12px 24px', borderRadius: 10, fontSize: 14, cursor: 'pointer' }}>View profile</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div style={{ padding: '20px 16px 18px' }}>
        <div className="container" style={{ padding: 0 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer', marginBottom: 12, padding: 0 }}>← Back</button>
          <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            {isManager ? 'Review a property management' : 'Review a tenant'}
          </div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(22px, 4.5vw, 28px)', fontWeight: 400, color: '#0F2C4E', lineHeight: 1.2, marginBottom: 6 }}>
            {isManager ? 'Find a property management or property manager' : 'Find a tenant or renter'}
          </h1>
          <p style={{ fontSize: 13, color: '#6B6358' }}>
            {googleReady ? 'Start typing — results appear automatically' : 'Loading search…'}
          </p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 24 }}>
        {!showAddForm && !selected && (
          <>
            <div className="form-group">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>City &amp; state <span style={{ color: '#6B6358', fontWeight: 400 }}>(optional)</span></span>
                <button type="button" onClick={handleGetLocation}
                  style={{ background: 'none', border: 'none', color: '#1A4C8B', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                  {locationLoading ? 'Getting...' : '📍 Use my location'}
                </button>
              </label>
              <input
                type="text" value={city} onChange={e => setCity(e.target.value)}
                placeholder="e.g. Los Angeles, CA  or  Austin, TX  or  leave blank for nationwide"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #E9ECEF', fontSize: 14, background: '#fff', outline: 'none' }}
              />
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>{isManager ? isManager ? 'Property Management or property management company' : 'Tenant full name' : 'Tenant full name'}</label>
              <input
                type="text" value={name}
                onChange={e => { setName(e.target.value); setSelected(null); }}
                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                placeholder={isManager ? isManager ? 'e.g. ABC Property Management, John Smith Rentals...' : 'e.g. John Smith...' : 'e.g. John Smith...'}
                autoComplete="off"
              />
              {showDropdown && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  background: '#FAF6EC', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(120,90,30,0.18)',
                  border: '1px solid rgba(184,137,58,0.30)', overflow: 'hidden', marginTop: 4,
                }}>
                  {suggestions.map((s, i) => (
                    <div key={i}
                      onMouseDown={() => handleSelectSuggestion(s)}
                      style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid rgba(184,137,58,0.18)' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F5EFE3'}
                      onMouseLeave={e => e.currentTarget.style.background = '#FAF6EC'}
                    >
                      <div style={{ fontWeight: 500, color: '#0F2C4E', fontSize: 13 }}>
                        {s.structured_formatting?.main_text || s.description?.split(',')[0]}
                      </div>
                      <div style={{ fontSize: 11, color: '#6B6358', marginTop: 2 }}>
                        {s.structured_formatting?.secondary_text || s.description?.split(',').slice(1).join(',').trim()}
                      </div>
                    </div>
                  ))}
                  <div
                    onMouseDown={() => {
                      if (!isManager && user?.role !== 'manager') { alert('Only property management/managers can add new tenants.'); setShowDropdown(false); return; }
                      setShowDropdown(false); setShowAddForm(true); setNewProfile({...newProfile, name});
                    }}
                    style={{ padding: '12px 16px', cursor: 'pointer', background: 'rgba(201,151,42,0.18)', fontSize: 13, color: '#0F2C4E', fontWeight: 700 }}
                  >
                    + Can't find them? Add to RenterRep
                  </div>
                </div>
              )}
            </div>

            {name.length >= 2 && !showDropdown && suggestions.length === 0 && (
              <div style={{ background: '#FDF6E7', border: '1px solid #C9972A', borderRadius: 12, padding: 16, marginTop: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E', marginBottom: 4 }}>0 results for "{name}"</div>
                <div style={{ fontSize: 12, color: '#6C757D', marginBottom: 12 }}>Add them to RenterRep and write your review!</div>
                <button onClick={() => {
                  if (!isManager && user?.role !== 'manager') { alert('Only property management/managers can add new tenants.'); return; }
                  setShowAddForm(true); setNewProfile({...newProfile, name, neighborhood: city});
                }}
                  style={{ background: '#C9972A', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  + Add to RenterRep
                </button>
              </div>
            )}
          </>
        )}

        {selected && !showAddForm && (
          <div>
            <div style={{ background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 16, boxShadow: '0 2px 8px rgba(120,90,30,0.06)', padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: '#0F2C4E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  {selected.initials || selected.name?.split(' ').slice(0,2).map(w=>w[0]).join('')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0F2C4E' }}>{selected.name}</div>
                  {selected.address && <div style={{ fontSize: 12, color: '#6B6358', marginTop: 2 }}>{selected.address}</div>}
                  {selected.phone && <div style={{ fontSize: 12, color: '#6B6358' }}>{selected.phone}</div>}
                </div>
              </div>
            </div>
            {selected.google ? (
              <div>
                <p style={{ fontSize: 13, color: '#6B6358', marginBottom: 16 }}>This manager isn't on RenterRep yet. Add them to write your review!</p>
                <button onClick={() => setShowAddForm(true)} className="btn btn-gold btn-lg" style={{ width: '100%' }}>Add to RenterRep & Review →</button>
                <button onClick={() => setSelected(null)} style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer' }}>← Search again</button>
              </div>
            ) : (
              <div>
                <button onClick={() => navigate(user ? '/review/' + selected.id : '/signup?redirect=' + encodeURIComponent('/review/' + selected.id))} className="btn btn-gold btn-lg" style={{ width: '100%' }}>Write a review →</button>
                <button onClick={() => navigate('/profile/' + selected.id)} style={{ width: '100%', marginTop: 10, background: 'transparent', border: '1.5px solid rgba(184,137,58,0.45)', borderRadius: 10, padding: '12px', fontSize: 13, cursor: 'pointer', color: '#6B6358' }}>View profile first</button>
                <button onClick={() => setSelected(null)} style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer' }}>← Search again</button>
              </div>
            )}
          </div>
        )}

        {showAddForm && (
          <>
            <button onClick={() => setShowAddForm(false)} style={{ background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer', marginBottom: 20 }}>← Back to search</button>
            <div style={{ background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 16, boxShadow: '0 2px 8px rgba(120,90,30,0.06)', padding: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0F2C4E', marginBottom: 4 }}>Add {isManager ? 'a manager or company' : 'a tenant'}</h2>
              <p style={{ fontSize: 12, color: '#6B6358', marginBottom: 20 }}>Only the name is required.</p>
              <form onSubmit={handleAdd}>
                <div className="form-group">
                  <label>{isManager ? 'Property Management or company name *' : 'Tenant full name *'}</label>
                  <input type="text" value={newProfile.name} onChange={e => setNewProfile({...newProfile, name: e.target.value})}
                    placeholder={isManager ? 'e.g. ABC Property Management...' : 'e.g. John Smith'} required />
                </div>
                {isManager && (
                  <>
                    <div className="form-group">
                      <label>Property address</label>
                      <input type="text" value={newProfile.address} onChange={e => setNewProfile({...newProfile, address: e.target.value})}
                        placeholder="e.g. 4605 Lankershim Blvd, North Hollywood, CA" />
                    </div>
                    <div className="form-group">
                      <label>Phone number</label>
                      <input type="text" value={newProfile.phone} onChange={e => setNewProfile({...newProfile, phone: e.target.value})}
                        placeholder="e.g. (818) 555-1234" />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input type="text" value={newProfile.neighborhood} onChange={e => setNewProfile({...newProfile, neighborhood: e.target.value})}
                        placeholder="e.g. North Hollywood" />
                    </div>
                    <div className="form-group">
                      <label>About (optional)</label>
                      <textarea value={newProfile.about} onChange={e => setNewProfile({...newProfile, about: e.target.value})}
                        placeholder="Brief description..." rows={3} />
                    </div>
                  </>
                )}
                <div style={{ background: '#F5EFE3', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#6B6358', lineHeight: 1.6 }}>
                  ℹ️ This profile will be publicly visible. Only add real managers or tenants you have had a rental relationship with.
                </div>
                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', opacity: adding ? 0.7 : 1 }} disabled={adding}>
                  {adding ? 'Adding...' : 'Add to RenterRep & write review →'}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
